import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; // For ngModel
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

// Import Angular Material modules
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';

import { Subscription } from 'rxjs';

@Component({
  selector: 'app-side-entity-form',
  standalone: true, // Make it standalone
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
 
  ],
  templateUrl: './side-entity-form.component.html',
  styleUrls: ['./side-entity-form.component.scss'] // Use styleUrls
})
export class SideEntityFormComponent implements OnInit, OnDestroy {
  @Input() data:any;

  get containerHeight(): string {
    const configured = this.data?.height || this.data?.chartConfig?.height;
    if (typeof configured === 'number') {
      return `${configured}px`;
    }
    if (typeof configured === 'string' && configured.trim()) {
      const normalized = configured.trim().toLowerCase();
      if (normalized.endsWith('vh') || normalized.endsWith('vw') || normalized.endsWith('%')) {
        return '100%';
      }
      return configured;
    }
    return '100%';
  }

  // Local copy of the record for editing
  editableRecord: any = null;
  originalRecord: any = null;
  formFields: { key: string, label: string }[] = [];
  sectionTitle: string = 'Details'; // Default title
  isSaving: boolean = false;
  loading: boolean = false;


  private recordFilterSubscription!: Subscription;
  private dropdownFilterSubscription!: Subscription;
  private loadingSubscription!: Subscription;
  private reflowSub?: Subscription;


  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private configService: ConfigService,
    private snackBar: MatSnackBar,
    private responsiveLayoutService: ResponsiveLayoutService,
    private cdr: ChangeDetectorRef) {

  }
  ngOnInit(): void {
    // Subscription for selected record from entity-table (widget1)
    const recordFilterListeners: string[] = this.data?.filterListner || [];
    if (recordFilterListeners.length > 0) {
      this.recordFilterSubscription = this.globalFilterService.filter$.subscribe(filterData => {
        if (recordFilterListeners.includes(filterData.changedFilterKey)) {
          const selectedRecord = filterData.requestParams[filterData.changedFilterKey];
          if (selectedRecord && selectedRecord.length > 0) {
            this.originalRecord = selectedRecord[0];
            this.editableRecord = { ...this.originalRecord };
            this.generateFormFieldsFromRecord();
          }
        }
      });
    }

    this.loadingSubscription = this.globalFilterService.filter$.subscribe(filterData => {
      if (filterData.widgetId === 'widget1' && filterData.changedFilterKey === 'loading') {
        const loadingState = filterData.requestParams['loading'];
        if (loadingState && loadingState.length > 0) {
          this.loading = loadingState[0];
        }
      }
    });

    // Subscription for dynamic section title from dropdown in entity-management
    // Assuming the dropdown key for 'Entity' is 'entity'
    this.dropdownFilterSubscription = this.globalFilterService.filter$.subscribe(filterData => {
      if (filterData.changedFilterKey === 'entity') { // Listen for 'entity' dropdown changes
        const selectedEntity = (filterData.queryParams?.['entity'] || filterData.requestParams?.['entity']);
        if (selectedEntity && selectedEntity.length > 0) {
          this.sectionTitle = selectedEntity[0]; // Update title with selected entity
        }
      }
    });

    // Initial record if provided
    if (this.data?.record) {
      this.originalRecord = this.data.record;
      this.editableRecord = { ...this.originalRecord };
      this.generateFormFieldsFromRecord();
    }
    
    // Set initial title from config if available, otherwise default
    if (this.data?.sections?.[0]?.title) {
      this.sectionTitle = this.data.sections[0].title;
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.cdr.detectChanges();
    });
  }
  
  protected generateFormFieldsFromRecord(): void {
    if (!this.editableRecord) {
      this.formFields = [];
      return;
    }
    
    this.formFields = Object.keys(this.editableRecord)
      .filter(key => key !== 'id') // Optional: filter out keys like '  '
      .map(key => ({
        key: key,
        label: this.toTitleCase(key)
      }));
  }

  protected toTitleCase(str: string): string {
    // This helper function converts snake_case and camelCase to Title Case
    str = str.replace(/_/g, ' ');
    str = str.replace(/([a-z])([A-Z])/g, ' $2');
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }

  ngOnDestroy(): void {
    if (this.recordFilterSubscription) {
      this.recordFilterSubscription.unsubscribe();
    }
    if (this.dropdownFilterSubscription) {
      this.dropdownFilterSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    this.reflowSub?.unsubscribe();
  }

  resetForm(): void {
    if (this.originalRecord) {
      this.editableRecord = { ...this.originalRecord }; // Reset from original
    } else {
      this.editableRecord = null;
    }
  }

  saveForm(action: any): void {
    if (!action.apiUrl) {
      console.error('Save API URL is not defined.');
      return;
    }

    this.isSaving = true;
    const method = action.method || 'POST';

    // Logic to check for changes or if it's a new record
    const isNewRecord = (this.originalRecord === null || this.originalRecord === undefined || this.originalRecord.id === null);

    const primaryId =
      this.originalRecord?.Code ??
      this.originalRecord?.code ??
      this.editableRecord?.Code ??
      this.editableRecord?.code ??
      null;

    if (!isNewRecord && (primaryId === null || primaryId === undefined || primaryId === '')) {
      this.isSaving = false;
      this.snackBar.open('Cannot save: primaryId (Code) is missing.', 'Close', { duration: 10000 });
      return;
    }

    const buildChangedFields = (editable: any, original: any): Record<string, any> => {
      const changed: Record<string, any> = {};
      const editableKeys = Object.keys(editable || {});
      for (const key of editableKeys) {
        // if (key === 'id') continue;
        if (editable?.[key] !== original?.[key]) {
          changed[key] = editable?.[key];
        }
      }
      return changed;
    };

    const changedFields = isNewRecord ? null : buildChangedFields(this.editableRecord, this.originalRecord);
    let hasChanges = false;

    if (!isNewRecord) {
      hasChanges = !!changedFields && Object.keys(changedFields).length > 0;
    } else {
      // For a new record, consider it as having changes if it's not empty
      hasChanges = Object.keys(this.editableRecord || {}).length > 0;
    }

    if (!hasChanges) {
      this.isSaving = false;
      this.snackBar.open('No changes to save.', 'Close', { duration: 10000 });
      return;
    }

    const dataForCreate = { ...(this.editableRecord || {}) };
    delete (dataForCreate as any).id;

    const payload = isNewRecord
      ? { data: dataForCreate }
      : { primaryId, data: changedFields };
  
    const globalFilters = this.globalFilterService.getCurrentFilters();
    const queryParams = { ...(globalFilters?.queryParams || {}) };
    if (!queryParams['entity']) {
      const entityFromRequestParams = globalFilters?.requestParams?.['entity'];
      if (entityFromRequestParams) queryParams['entity'] = entityFromRequestParams;
    }

    const filtersToSend = { queryParams, requestParams: payload };
    const requestConfig = { ...this.data, ...action, skipDefaultPayload: true };

    console.log('Saving data:', payload);

    const apiUrl = (() => {
      if (!isNewRecord) return action.apiUrl;
      const trimmed = String(action.apiUrl).endsWith('/') ? String(action.apiUrl).slice(0, -1) : String(action.apiUrl);
      if (trimmed.endsWith('/list')) return trimmed.replace(/\/list$/, '/create');
      if (trimmed.endsWith('/update')) return trimmed.replace(/\/update$/, '/create');
      return `${trimmed}/create`;
    })();

    this.configService.getApi(apiUrl, filtersToSend, requestConfig, method).subscribe({
      next: (response) => {
        this.isSaving = false;

        const statusValue = typeof response?.status === 'string' ? response.status.toLowerCase() : null;
        const isSuccess = statusValue ? statusValue === 'success' : true;
        if (!isSuccess) {
          const message =
            response?.ui_response ||
            response?.error ||
            response?.detail ||
            'Save failed.';
          console.error('Save failed', response);
          this.snackBar.open(String(message), 'Close', {
            duration: 10000,
            panelClass: ['error-snackbar']
          });
          return;
        }

        console.log('Save successful', response);
        this.snackBar.open('Saved successfully!', 'Close', {
          duration: 10000,
          panelClass: ['success-snackbar']
        });
        // After saving, update the original record to match the new state
        this.originalRecord = { ...this.editableRecord };
        if (action.refreshWidgets && Array.isArray(action.refreshWidgets)) {
          action.refreshWidgets.forEach((widgetId: string) => {
            this.globalFilterService.requestWidgetRefresh(widgetId);
          });
        }
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Save failed', error);
        this.snackBar.open('Error saving data.', 'Close', {
          duration: 10000,
        });
      },
    });
  }

  handleAction(actionId: string): void {
    const action = this.data?.sections
      ?.flatMap((s: any) => s.actions)
      .find((a: any) => a.actionId === actionId);

    if (action) {
      if (actionId === 'reset') {
        this.resetForm();
      } else if (actionId === 'save') {
        this.saveForm(action);
      }
    }
  }
}
